import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TimelineDataPoint } from './timelineUtils';
import styles from './SocialGraphPOC.module.scss';

interface TimelineOverviewStripProps {
  /** the FULL dataset — the strip always shows the whole period */
  data: TimelineDataPoint[];
  visibleTypes: string[];
  /** inclusive bucket-index window into `data` */
  window: [number, number];
  /** emits the new window on release / keypress; the page normalizes a
   *  full-extent window back to null (= no time filter) */
  onWindowChange: (window: [number, number]) => void;
  /** dimmed and inert while playback runs (Figma strip state "Locked") */
  locked: boolean;
}

type DragMode = 'move' | 'left' | 'right';

interface DragState {
  mode: DragMode;
  startClientX: number;
  startWindow: [number, number];
  window: [number, number];
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

// Overview strip under the timeline chart (Figma Timeline / Overview strip 503-34):
// flattened silhouette of the whole period + a window frame with two resize grips.
// Drag the frame to move the window, drag a grip to resize, arrow keys nudge.
export const TimelineOverviewStrip: React.FC<TimelineOverviewStripProps> = ({
  data,
  visibleTypes,
  window: windowProp,
  onWindowChange,
  locked,
}) => {
  const { t } = useTranslation();
  const trackRef = useRef<HTMLDivElement>(null);
  // window rendered live while dragging; committed to the page on release so
  // the graph/chart don't refilter on every pointermove
  const [drag, setDrag] = useState<DragState | null>(null);

  const count = data.length;
  const [start, end] = drag ? drag.window : windowProp;

  const totals = useMemo(
    () =>
      data.map((point) =>
        visibleTypes.reduce((sum, type) => sum + (point.byType[type] ?? 0), 0),
      ),
    [data, visibleTypes],
  );
  const maxTotal = useMemo(() => Math.max(1, ...totals), [totals]);

  const applyDrag = (state: DragState, clientX: number): DragState => {
    const trackWidth = trackRef.current?.offsetWidth ?? 0;
    if (trackWidth === 0) return state;
    const delta = Math.round(((clientX - state.startClientX) / trackWidth) * count);
    const [s0, e0] = state.startWindow;
    let next: [number, number];
    if (state.mode === 'move') {
      const shift = clamp(delta, -s0, count - 1 - e0);
      next = [s0 + shift, e0 + shift];
    } else if (state.mode === 'left') {
      next = [clamp(s0 + delta, 0, e0), e0];
    } else {
      next = [s0, clamp(e0 + delta, s0, count - 1)];
    }
    return { ...state, window: next };
  };

  const handlePointerDown = (mode: DragMode) => (event: React.PointerEvent<HTMLElement>) => {
    if (locked) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    setDrag({
      mode,
      startClientX: event.clientX,
      startWindow: windowProp,
      window: windowProp,
    });
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLElement>) => {
    if (!drag) return;
    setDrag((state) => (state ? applyDrag(state, event.clientX) : state));
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLElement>) => {
    if (!drag) return;
    const finished = applyDrag(drag, event.clientX);
    setDrag(null);
    onWindowChange(finished.window);
  };

  // cancelled gesture (touch scroll takeover, context menu, page hide): discard
  // the uncommitted preview instead of freezing the strip in drag state
  const handlePointerCancel = () => setDrag(null);

  const handleKey = (mode: DragMode) => (event: React.KeyboardEvent<HTMLElement>) => {
    if (locked) return;
    let step: number;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') step = -1;
    else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') step = 1;
    else if (event.key === 'Home') step = -count;
    else if (event.key === 'End') step = count;
    else return;
    event.preventDefault();
    let next: [number, number];
    if (mode === 'move') {
      const shift = clamp(step, -start, count - 1 - end);
      next = [start + shift, end + shift];
    } else if (mode === 'left') {
      next = [clamp(start + step, 0, end), end];
    } else {
      next = [start, clamp(end + step, start, count - 1)];
    }
    if (next[0] !== start || next[1] !== end) onWindowChange(next);
  };

  if (count === 0) return null;

  const pct = (index: number) => `${(index / count) * 100}%`;
  const tabIndex = locked ? -1 : 0;

  return (
    <div className={`${styles.timelineStrip} ${locked ? styles.timelineStripLocked : ''}`}>
      <div ref={trackRef} className={styles.timelineStripTrack}>
        {/* flattened silhouette of the whole period; bars keep the bottom 6px free
            for the window frame's inset, mirroring the Figma layer geometry */}
        <svg
          className={styles.timelineStripSilhouette}
          viewBox={`0 0 ${count} 26`}
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          {totals.map((total, i) =>
            total > 0 ? (
              <rect
                key={data[i].key}
                x={i + 0.1}
                width={0.8}
                y={20 - Math.max(1, (total / maxTotal) * 18)}
                height={Math.max(1, (total / maxTotal) * 18)}
              />
            ) : null,
          )}
        </svg>
        <div
          className={styles.timelineStripWindow}
          style={{ left: pct(start), width: pct(end - start + 1) }}
          role="slider"
          aria-label={t('Visible range')}
          aria-orientation="horizontal"
          aria-valuemin={0}
          aria-valuemax={count - 1}
          aria-valuenow={start}
          aria-valuetext={`${data[start].label} – ${data[end].label}`}
          aria-disabled={locked || undefined}
          tabIndex={tabIndex}
          onPointerDown={handlePointerDown('move')}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
          onKeyDown={handleKey('move')}
        />
        <div
          className={styles.timelineStripGrip}
          style={{ left: pct(start) }}
          role="slider"
          aria-label={t('Range start')}
          aria-orientation="horizontal"
          aria-valuemin={0}
          aria-valuemax={end}
          aria-valuenow={start}
          aria-valuetext={data[start].label}
          aria-disabled={locked || undefined}
          tabIndex={tabIndex}
          onPointerDown={handlePointerDown('left')}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
          onKeyDown={handleKey('left')}
        />
        <div
          className={`${styles.timelineStripGrip} ${styles.timelineStripGripEnd}`}
          style={{ left: pct(end + 1) }}
          role="slider"
          aria-label={t('Range end')}
          aria-orientation="horizontal"
          aria-valuemin={start}
          aria-valuemax={count - 1}
          aria-valuenow={end}
          aria-valuetext={data[end].label}
          aria-disabled={locked || undefined}
          tabIndex={tabIndex}
          onPointerDown={handlePointerDown('right')}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
          onKeyDown={handleKey('right')}
        />
      </div>
    </div>
  );
};
